import React from 'react'
import { useLocation } from 'react-router-dom'
import { Socket } from 'socket.io-client'
import { IChat, IChatListUpdatedPayload, IChatUnreadCountUpdatedPayload } from 'models/chat'
import { TOKEN } from 'shared/constants/app'
import { adminRoutes } from 'shared/constants/routes'
import { createChatSocket } from 'shared/utils/chatSocket'
import { showBrowserNotification } from 'shared/utils/notifications'
import { getUserName } from 'components/chats/utils'
import { chatActions, getChatUnreadCount } from 'store/chat'
import { useAppDispatch } from './useReduxHooks'

const CHATS_PATH = `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_CHATS}`

const getIncomingMessage = (chat: IChat) => {
  const isSupport = chat.type === 'SUPPORT'
  const isIncoming = chat.last_message_sender_type === (isSupport ? 'SUPPORT' : 'USER')
  const unreadCount = isSupport ? chat.unread_for_user : chat.unread_for_seller

  if (!isIncoming || !unreadCount || !chat.last_message_at) return null

  return {
    key: chat.last_message_at,
    title: isSupport ? 'Команда поддержки' : getUserName(chat.user_id),
    body: chat.last_message_text || 'Новое сообщение'
  }
}

// Счётчик непрочитанных и уведомления продавца о новых сообщениях.
// Хук зовём из обёртки приложения: она смонтирована всегда, в том числе на телефоне
export const useSellerChatRealtime = (isEnabled: boolean) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const socketRef = React.useRef<Socket | null>(null)
  const pathnameRef = React.useRef(location.pathname)
  // Одно и то же событие приходит и когда сообщение написали, и когда его
  // прочитали на другой стороне — уведомляем один раз на сообщение
  const notifiedRef = React.useRef<Record<string, string>>({})

  React.useEffect(() => {
    pathnameRef.current = location.pathname
  }, [location.pathname])

  React.useEffect(() => {
    if (!isEnabled) return undefined

    dispatch(getChatUnreadCount())

    if (!localStorage.getItem(TOKEN)) return undefined

    const socket = createChatSocket()
    socketRef.current = socket

    let hasConnected = false
    socket.on('connect', () => {
      if (hasConnected) dispatch(getChatUnreadCount())
      hasConnected = true
    })
    socket.on('chat_unread_count_updated', (payload: IChatUnreadCountUpdatedPayload) => {
      dispatch(chatActions.setUnreadCount(payload.unreadCount))
    })

    socket.on('chat_list_updated', (payload: IChatListUpdatedPayload) => {
      const incoming = getIncomingMessage(payload.chat)
      if (!incoming) return

      if (notifiedRef.current[payload.chat._id] === incoming.key) return
      notifiedRef.current[payload.chat._id] = incoming.key

      // Переписка открыта на экране — уведомление только помешает
      const isChatsOpen =
        document.visibilityState === 'visible' && pathnameRef.current.startsWith(CHATS_PATH)
      if (isChatsOpen) return

      void showBrowserNotification({
        title: incoming.title,
        body: incoming.body,
        tag: `horsesmart-chat-${payload.chat._id}`,
        url: CHATS_PATH
      })
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [dispatch, isEnabled])
}
